import { Component, ElementRef, inject, Renderer2 } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AppService } from './app.service';
import { _resetForcedValuePatching, AppEnvironmentEnum, AppIdEnum, getDatetime, hasDebugging, LibSharedSpinnerComponent, VersionBannerComponent } from '@lib/shared';
import { MatButton } from '@angular/material/button';
import { RequestComponent } from './components/request/request.component';
import { SuccessComponent } from './components/success/success.component';
import { FailureComponent } from './components/failure/failure.component';
import { filter } from 'rxjs';

@Component({
	selector: 'request-root',
	// prettier-ignore
	imports: [
		CommonModule,
		FormsModule,
		LibSharedSpinnerComponent,
		MatButton,
		NgOptimizedImage,
		ReactiveFormsModule,
		RouterLink,
		TranslateModule,
		VersionBannerComponent,
		RequestComponent,
		SuccessComponent,
		FailureComponent
	],
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss']
})
export class AppComponent {
	protected readonly environment = environment;
	protected readonly AppEnvironmentEnum = AppEnvironmentEnum;

	private readonly router = inject(Router);
	private readonly elementRef = inject(ElementRef);
	private readonly renderer = inject(Renderer2);
	private readonly translateService = inject(TranslateService);

	readonly appService = inject(AppService);

	public get hasDebugging(): boolean {
		return hasDebugging(environment.appEnvironment);
	}

	getCurrentLanguage = this.appService.getCurrentLanguage;

	private lastMinuteHintVisible = false;

	constructor() {
		// prettier-ignore
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event: any) => {
				const lang = event.url.slice(1) ? event.url.slice(1) : this.appService.getDefaultLanguage();
				console.log('App language by path', lang);
				this.appService.setLanguage(lang);
			});

		this.appService.requestOptions = {
			appEnvironment: environment.appEnvironment,
			appId: AppIdEnum.REQUEST,
			minimumBookingPeriodNotReachedText: this.appService.getTranslation('Mindestbuchungsdauer beträgt 5h!'),
			setLastMinuteHintFn: this.setLastMinuteHint
		};

		this.appService.resetApp();

		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			this.displayLastMinuteHint();
		});
	}

	setLastMinuteHint = (value: any): void => {
		this.lastMinuteHintVisible = value === 'LastMinuteSurcharge';
		this.displayLastMinuteHint();
	};

	displayLastMinuteHint(): void {
		const node = this.elementRef.nativeElement.querySelector('#personal\\.summen\\.last_minute_text');
		if (node) {
			if (this.lastMinuteHintVisible) {
				this.renderer.setProperty(node, 'innerHTML', this.appService.getTranslation('Bei einer Bestellung ab 14 Tage vor dem ersten Einsatztag wird ein Last&nbsp;Minute&nbsp;Zuschlag in Höhe von 25% auf den Auftrag erhoben.'));
				this.renderer.setStyle(node, 'display', 'flex');
			} else {
				this.renderer.setStyle(node, 'display', 'none');
				this.renderer.setProperty(node, 'innerHTML', '');
			}
		}
	}

	mockForm(): void {
		const data = {
			veranstaltung: {
				veranstaltungsname: 'Testmesse ' + getDatetime(),
				hallennummer: 'Halle 1',
				standnummer: 'Stand 1'
			},
			aussteller: {
				firmenname: 'Testfirma GmbH',
				strasse: 'Hauptstraße',
				hausnummer: '123',
				plz: '90402',
				ort: 'Nürnberg',
				land: 'DE',
				ustId: 'DE123456789',
				abweichende_rechnungsanschrift: true,
				rechnungsanschrift: {
					firmenname: 'Testholding AG',
					strasse: 'Maximilianstraße',
					hausnummer: ' 1',
					plz: '80539',
					ort: 'München',
					land: 'DE',
					ustId: 'DE987654321'
				}
			},
			ansprechpartner: {
				anrede: '2',
				nachname: 'Mustermann',
				vorname: 'Max',
				email: 'max.muster@firma.gibtsnicht',
				telefon: '0987654321',
				ansprechpartner_vor_ort_vorhanden: true,
				ansprechpartner_vor_ort: {
					anrede: '1',
					nachname: 'Musterfrau',
					vorname: 'Anna',
					email: 'anna.musterfrau@holding.gibtsnicht',
					telefon: '0123456789'
				}
			}
		};

		const requestForm = this.appService.getGroup();
		if (requestForm) {
			requestForm.forcedValuePatching = true;
			requestForm.patchValue(data, { emitEvent: true });
			_resetForcedValuePatching(requestForm);
		}
	}
}
